/**
 * ENG-1901: make the Alerts page say which group is in scope.
 *
 * Parent accounts can nest (TV Wall under Genesis) but the picker used to
 * render them as peers. These helpers turn that flat list into a tree, decide
 * when the selection is narrower than the account, and build the copy for the
 * scope line and the empty state.
 */

export interface ScopeGroup {
  id: number;
  name: string;
  parent?: number | null;
  site_count?: number | null;
}

export interface ScopePickerOption {
  id: number;
  name: string;
  depth: number;
  path: string;
  siteCount: number | null;
  parentId: number | null;
}

export interface ResolvedAlertScope {
  narrower: boolean;
  selected: ScopePickerOption | null;
  accountRoot: ScopePickerOption | null;
}

export interface OtherAlertsCount {
  count: number;
  /** True when either query hit the result cap, so `count` is a floor. */
  atLeast: boolean;
  /**
   * False when the cap makes a zero difference meaningless — we must not
   * tell the operator the rest of the account is empty.
   */
  known: boolean;
}

const PATH_SEPARATOR = " › ";

const asId = (value: unknown): number | null => {
  if (value == null || value === "") {
    return null;
  }
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const byName = (a: ScopeGroup, b: ScopeGroup) => a.name.localeCompare(b.name);

export const buildScopePickerOptions = (
  groups: ScopeGroup[]
): ScopePickerOption[] => {
  const byId = new Map<number, ScopeGroup>();
  (groups ?? []).forEach((group) => {
    if (group && typeof group.id === "number" && group.name) {
      byId.set(group.id, group);
    }
  });

  const children = new Map<number, ScopeGroup[]>();
  const roots: ScopeGroup[] = [];
  byId.forEach((group) => {
    const parentId = asId(group.parent);
    if (parentId != null && parentId !== group.id && byId.has(parentId)) {
      const list = children.get(parentId) ?? [];
      list.push(group);
      children.set(parentId, list);
    } else {
      roots.push(group);
    }
  });

  roots.sort(byName);
  children.forEach((list) => list.sort(byName));

  const options: ScopePickerOption[] = [];
  const visit = (
    group: ScopeGroup,
    depth: number,
    ancestors: string[],
    seen: Set<number>
  ) => {
    if (seen.has(group.id)) {
      return;
    }
    seen.add(group.id);
    const pathNames = [...ancestors, group.name];
    options.push({
      id: group.id,
      name: group.name,
      depth,
      path: pathNames.join(PATH_SEPARATOR),
      siteCount: typeof group.site_count === "number" ? group.site_count : null,
      parentId: asId(group.parent),
    });
    (children.get(group.id) ?? []).forEach((child) =>
      visit(child, depth + 1, pathNames, seen)
    );
  };

  const seen = new Set<number>();
  roots.forEach((root) => visit(root, 0, [], seen));
  // A parent cycle has no root. Visit whatever the walk never reached so those
  // groups still appear, instead of disappearing from the picker.
  byId.forEach((group) => {
    if (!seen.has(group.id)) {
      visit(group, 0, [], seen);
    }
  });
  return options;
};

export const resolveAlertScope = (
  groups: ScopeGroup[],
  selectedId: string | number | null | undefined
): ResolvedAlertScope => {
  const id = asId(selectedId);
  if (id == null || selectedId === "undefined") {
    return { narrower: false, selected: null, accountRoot: null };
  }
  const options = buildScopePickerOptions(groups);
  const byId = new Map(options.map((option) => [option.id, option]));
  const selected = byId.get(id) ?? null;
  if (!selected) {
    return { narrower: false, selected: null, accountRoot: null };
  }

  let root = selected;
  const seen = new Set<number>();
  while (
    root.parentId != null &&
    byId.has(root.parentId) &&
    !seen.has(root.id)
  ) {
    seen.add(root.id);
    const parent = byId.get(root.parentId);
    if (!parent) {
      break;
    }
    root = parent;
  }

  return {
    narrower: selected.id !== root.id,
    selected,
    accountRoot: root,
  };
};

export const diffAlertCounts = (
  accountCount: number,
  groupCount: number,
  accountLimited: boolean,
  groupLimited: boolean
): OtherAlertsCount => {
  const count = Math.max(0, accountCount - groupCount);
  const atLeast = accountLimited || groupLimited;
  return {
    count,
    atLeast,
    known: !(atLeast && count === 0),
  };
};

export const formatSiteCount = (siteCount: number | null): string | null => {
  if (siteCount == null) {
    return null;
  }
  return siteCount === 1 ? "1 site" : `${siteCount} sites`;
};

export const formatOtherAlertsLabel = (
  other: OtherAlertsCount | null
): string | null => {
  if (!other || !other.known) {
    return null;
  }
  if (other.atLeast) {
    return `at least ${other.count} other alerts`;
  }
  return other.count === 1 ? "1 other alert" : `${other.count} other alerts`;
};

const timestampToDate = (timestamp: number | null | undefined): Date | null => {
  if (
    typeof timestamp !== "number" ||
    !Number.isFinite(timestamp) ||
    timestamp <= 0
  ) {
    return null;
  }
  const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dateFromFilter = (
  startDate: Date | string | null | undefined,
  startTime: Date | string | null | undefined
): Date | null => {
  if (!startDate) {
    return null;
  }
  const date =
    startDate instanceof Date
      ? new Date(startDate.getTime())
      : new Date(startDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  if (startTime) {
    const time = startTime instanceof Date ? startTime : new Date(startTime);
    if (!Number.isNaN(time.getTime())) {
      date.setHours(time.getHours(), time.getMinutes(), 0, 0);
    }
  }
  return date;
};

const formatClock = (date: Date): string => {
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const suffix = date.getHours() >= 12 ? "PM" : "AM";
  const hour = date.getHours() % 12 || 12;
  return `${hour}:${minutes} ${suffix}`;
};

const formatSince = (date: Date, now: Date): string => {
  const time = formatClock(date);
  if (date.toDateString() === now.toDateString()) {
    return time;
  }
  const day = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${day}, ${time}`;
};

export const formatWindowSince = ({
  minStartTimestamp,
  startDate,
  startTime,
  periodHours,
  now = new Date(),
}: {
  minStartTimestamp?: number | null;
  startDate?: Date | string | null;
  startTime?: Date | string | null;
  periodHours: number;
  now?: Date;
}): string => {
  const fromResponse = timestampToDate(minStartTimestamp);
  const fromFilter = dateFromFilter(startDate, startTime);
  const hours =
    Number.isFinite(periodHours) && periodHours > 0 ? periodHours : 2;
  const start =
    fromResponse ??
    fromFilter ??
    new Date(now.getTime() - hours * 60 * 60 * 1000);
  return formatSince(start, now);
};

const alertsArrivedPhrase = (other: OtherAlertsCount): string => {
  if (other.atLeast) {
    return `at least ${other.count} alerts arrived`;
  }
  const noun = other.count === 1 ? "alert" : "alerts";
  return `${other.count} ${noun} arrived`;
};

/**
 * Copy for an empty grid. Returns null when a site or camera filter is on and
 * we cannot prove alerts exist outside this group — that case keeps the
 * generic "selected filters" sentence so we don't claim the account is empty.
 */
export const scopeEmptyMessage = ({
  narrower,
  groupName,
  accountName,
  sinceLabel,
  other,
  hasSiteOrCameraFilter,
}: {
  narrower: boolean;
  groupName: string | null;
  accountName: string | null;
  sinceLabel: string;
  other: OtherAlertsCount | null;
  hasSiteOrCameraFilter: boolean;
}): string | null => {
  const othersKnown = Boolean(other?.known && other.count > 0);
  if (hasSiteOrCameraFilter && !othersKnown) {
    return null;
  }

  const since = `since ${sinceLabel}`;
  const accountIsEmpty =
    !narrower || (other?.known && other.count === 0 && !other.atLeast);

  if (accountIsEmpty) {
    return `No alerts in your account ${since}.`;
  }

  if (othersKnown && groupName && accountName && other) {
    return `No alerts for ${groupName} ${since}. ${alertsArrivedPhrase(
      other
    )} for other ${accountName} groups in the same window.`;
  }

  if (narrower && groupName) {
    return `No alerts for ${groupName} ${since}.`;
  }

  return `No alerts in your account ${since}.`;
};

/** Swap group_id on an alert search string, keeping every other filter. */
export const replaceGroupId = (
  searchParamsString: string,
  groupId: number
): string => {
  const raw = searchParamsString.startsWith("&")
    ? searchParamsString.slice(1)
    : searchParamsString;
  const params = new URLSearchParams(raw);
  params.set("group_id", String(groupId));
  const next = params.toString();
  return next ? `&${next}` : "";
};
